import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useIncidentStore } from '@/store/incident.store';
import { api } from '@/services/api';
import { useActiveIncidents } from '@/hooks/useIncidents';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { Incident, STATUS_COLORS, OperationalPeriod } from '@/types/incident';
import { NextUpItem } from '@/types/ics-support';

import FaIcon from '@/components/common/FaIcon';
import { AlertIcon, CalendarIcon, CheckIcon } from '../icons';

/**
 * How long each upcoming schedule event stays on screen before the next.
 * Long enough to read a briefing title at a glance without the sidebar becoming
 * a distraction on a wall display.
 */
const NEXT_UP_ROTATE_MS = 5000;

function ChevronIcon({ className, isOpen }: { className?: string; isOpen: boolean }) {
  return (
    <FaIcon icon="chevron-down" className={`${className} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
  );
}

/** 24h clock rendering for mono instrument values (e.g. "02:00"). */
function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
}

/** Compact 24h period range for the OP anchor line (e.g. "06:00–18:00"). */
function formatOpPeriodTimeRange(start: string, end?: string): string {
  const startTime = formatTime(start);
  if (!end) return startTime;
  return `${startTime}–${formatTime(end)}`;
}

export default function IncidentSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentOpPeriod, setCurrentOpPeriod] = useState<OperationalPeriod | null>(null);
  const [nextUpEvents, setNextUpEvents] = useState<NextUpItem[]>([]);
  const [nextUpIndex, setNextUpIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { activeIncident, isDailyOps, setActiveIncident, setDailyOps } = useIncidentStore();
  const { data: incidents = [], isLoading: incidentsLoading } = useActiveIncidents();

  // Exercise (sandboxed) incidents are excluded from the normal incidents list
  // by the backend, so fetch them separately for a distinct badged section.
  const { data: exerciseResp } = useTenantQuery<{ data: Incident[] }>(
    ['incidents', { exercise_only: true, status: 'active,stabilizing,recovery,planned' }],
    '/incidents',
    { params: { exercise_only: true, status: 'active,stabilizing,recovery,planned', limit: 50 } },
  );
  const exerciseIncidents = exerciseResp?.data ?? [];

  // Load current operational period and upcoming events when active incident changes
  useEffect(() => {
    if (activeIncident?.id && !isDailyOps) {
      loadOpPeriodAndEvents(activeIncident.id);
    } else {
      setCurrentOpPeriod(null);
      setNextUpEvents([]);
    }
  }, [activeIncident?.id, isDailyOps]);

  const loadOpPeriodAndEvents = async (incidentId: string) => {
    try {
      const [nextUpRes, incidentRes] = await Promise.all([
        api.get<NextUpItem[]>(`/incidents/${incidentId}/next-up?limit=3`),
        api.get<{ current_operational_period?: OperationalPeriod }>(`/incidents/${incidentId}`),
      ]);
      setCurrentOpPeriod(incidentRes.current_operational_period || null);
      setNextUpEvents(nextUpRes);
    } catch (err) {
      console.error('Error loading operational period and events:', err);
      setCurrentOpPeriod(null);
      setNextUpEvents([]);
    }
  };

  // Close dropdown on outside click; Escape closes and returns focus to the
  // trigger (keyboard contract). ArrowDown/ArrowUp cycle through menu items
  // (wrapping), Home/End jump to first/last, Enter activates natively.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
        const items = Array.from(
          dropdownRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
        );
        if (items.length === 0) return;
        event.preventDefault();
        const currentIndex = items.indexOf(document.activeElement as HTMLElement);
        let nextIndex: number;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = items.length - 1;
        else if (event.key === 'ArrowDown') nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
        else nextIndex = currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex]?.focus();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelectIncident = (incident: Incident) => {
    setActiveIncident(incident);
    setIsOpen(false);

    // Update user context on server
    api.put('/user/context', {
      incident_id: incident.id,
      is_daily_ops: false,
    }).catch(console.error);
  };

  const handleSelectDailyOps = () => {
    setDailyOps();
    setIsOpen(false);

    // Update user context on server
    api.put('/user/context', {
      is_daily_ops: true,
    }).catch(console.error);
  };

  // Filter to show only active, stabilizing, and recovery incidents
  const activeIncidents = incidents.filter(
    (i) => i.status === 'active' || i.status === 'stabilizing' || i.status === 'recovery'
  );
  const plannedIncidents = incidents.filter((i) => i.status === 'planned');

  const getStatusBadge = (status: Incident['status']) => {
    const colors = STATUS_COLORS[status];
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
        {status === 'active' ? 'Active' : status === 'stabilizing' ? 'Stabilizing' : status === 'recovery' ? 'Recovery' : 'Planned'}
      </span>
    );
  };

  // Upcoming schedule events (meals, briefings, shift changes) — subordinate to
  // the operational-period anchor line. The line is one row tall, so it cycles
  // through them rather than showing only nextUpEvents[0] and hiding the rest,
  // which is what it did before: everything after the first was fetched and then
  // never displayed.
  const nextUp = nextUpEvents[nextUpIndex] ?? nextUpEvents[0];

  // Reset to the first event whenever the list changes, so a refetch cannot
  // leave the index pointing past the end (or at an unrelated event).
  useEffect(() => {
    setNextUpIndex(0);
  }, [nextUpEvents]);

  useEffect(() => {
    if (nextUpEvents.length < 2) return;
    const id = setInterval(() => {
      setNextUpIndex((i) => (i + 1) % nextUpEvents.length);
    }, NEXT_UP_ROTATE_MS);
    return () => clearInterval(id);
  }, [nextUpEvents.length]);

  return (
    <div className="relative px-4 py-2" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
          isDailyOps || !activeIncident
            ? 'border-white/[0.08] bg-white/[0.04] text-white hover:bg-white/[0.08]'
            : 'bg-primary-900/50 text-primary-100 hover:bg-primary-900/70 border-primary-700'
        }`}
      >
        {isDailyOps ? (
          <CalendarIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <AlertIcon className="h-5 w-5 text-primary-400" />
        )}
        <span className="flex-1 truncate">
          {isDailyOps ? (
            'Daily Operations'
          ) : activeIncident ? (
            <>
              {activeIncident.is_exercise && (
                <span className="mr-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-900/50 text-amber-300 align-middle">
                  Exercise
                </span>
              )}
              <span className="font-mono text-[0.94em] font-medium">{activeIncident.incident_number}</span>
              {' - '}
              {activeIncident.short_name || activeIncident.name}
            </>
          ) : (
            'Select Context'
          )}
        </span>
        <ChevronIcon className="h-4 w-4 text-gray-400" isOpen={isOpen} />
      </button>

      {/* Operational period anchor + next-up schedule line */}
      {!isDailyOps && activeIncident && (currentOpPeriod || nextUp) && (
        <div className="mt-2 rounded-md border border-white/[0.08] bg-white/[0.04] px-3 py-2">
          {currentOpPeriod && (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-xs font-semibold text-white flex-shrink-0">
                OP {currentOpPeriod.period_number}
              </span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                  currentOpPeriod.status === 'active'
                    ? 'bg-green-900/50 text-green-300'
                    : currentOpPeriod.status === 'planned'
                    ? 'bg-gray-700 text-gray-300'
                    : 'bg-yellow-900/50 text-yellow-300'
                }`}
              >
                {currentOpPeriod.status}
              </span>
              <span className="ml-auto font-mono text-[11px] text-gray-400 truncate">
                {formatOpPeriodTimeRange(currentOpPeriod.start_time, currentOpPeriod.end_time)}
              </span>
            </div>
          )}
          {nextUp && (
            <div
              className={`flex items-center gap-1.5 font-mono text-[11px] text-gray-400 min-w-0 ${
                currentOpPeriod ? 'mt-1' : ''
              }`}
              title={
                nextUpEvents.length > 1
                  ? `Upcoming (${nextUpIndex + 1} of ${nextUpEvents.length}): ${nextUp.title}`
                  : `Next up: ${nextUp.title}`
              }
              aria-live="polite"
            >
              <span aria-hidden="true" className="flex-shrink-0">&#9656;</span>
              <span className="truncate">{nextUp.title}</span>
              <span className="flex-shrink-0">&middot; {formatTime(nextUp.scheduled_time)}</span>
              {nextUpEvents.length > 1 && (
                <span className="flex-shrink-0 text-gray-500">
                  {nextUpIndex + 1}/{nextUpEvents.length}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {isOpen && (
        <div
          role="menu"
          className="absolute left-4 right-4 z-50 mt-1 rounded-md bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 max-h-96 overflow-y-auto scrollbar-dark"
        >
          {/* Daily Operations Option */}
          <button
            type="button"
            role="menuitem"
            onClick={handleSelectDailyOps}
            className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            <CalendarIcon className="h-4 w-4 text-theme-tertiary" />
            <span className="flex-1 text-left">Daily Operations</span>
            {isDailyOps && <CheckIcon className="h-4 w-4 text-primary-400" />}
          </button>

          {/* Divider */}
          {(activeIncidents.length > 0 || plannedIncidents.length > 0) && (
            <div className="border-t border-gray-700 my-1" />
          )}

          {/* Active Incidents */}
          {activeIncidents.length > 0 && (
            <>
              <p className="px-3 py-1 text-xs text-theme-tertiary uppercase tracking-wide">Active Incidents</p>
              {activeIncidents.map((incident) => (
                <button
                  key={incident.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSelectIncident(incident)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                >
                  <AlertIcon className="h-4 w-4 text-primary-400" />
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[0.94em] font-medium truncate">
                        {incident.incident_number}
                      </span>
                      {getStatusBadge(incident.status)}
                    </div>
                    <span className="text-xs text-theme-tertiary truncate block">
                      {incident.short_name || incident.name}
                    </span>
                  </div>
                  {activeIncident?.id === incident.id && (
                    <CheckIcon className="h-4 w-4 text-primary-400 shrink-0" />
                  )}
                </button>
              ))}
            </>
          )}

          {/* Planned Incidents */}
          {plannedIncidents.length > 0 && (
            <>
              <p className="px-3 py-1 text-xs text-theme-tertiary uppercase tracking-wide mt-1">Planned</p>
              {plannedIncidents.slice(0, 3).map((incident) => (
                <button
                  key={incident.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSelectIncident(incident)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                >
                  <AlertIcon className="h-4 w-4 text-primary-500" />
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[0.94em] font-medium truncate">
                        {incident.incident_number}
                      </span>
                      {getStatusBadge(incident.status)}
                    </div>
                    <span className="text-xs text-theme-tertiary truncate block">
                      {incident.short_name || incident.name}
                    </span>
                  </div>
                  {activeIncident?.id === incident.id && (
                    <CheckIcon className="h-4 w-4 text-primary-400 shrink-0" />
                  )}
                </button>
              ))}
            </>
          )}

          {/* Exercises (sandboxed) — distinct badged section */}
          {exerciseIncidents.length > 0 && (
            <>
              <div className="border-t border-gray-700 my-1" />
              <p className="px-3 py-1 text-xs text-theme-tertiary uppercase tracking-wide flex items-center gap-1.5">
                <FaIcon icon="flask" className="h-3 w-3" />
                Exercises
              </p>
              {exerciseIncidents.map((incident) => (
                <button
                  key={incident.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSelectIncident(incident)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                >
                  <FaIcon icon="flask" className="h-4 w-4 text-amber-400" />
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[0.94em] font-medium truncate">
                        {incident.incident_number}
                      </span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-900/50 text-amber-300">
                        Exercise
                      </span>
                    </div>
                    <span className="text-xs text-theme-tertiary truncate block">
                      {incident.short_name || incident.name}
                    </span>
                  </div>
                  {activeIncident?.id === incident.id && (
                    <CheckIcon className="h-4 w-4 text-primary-400 shrink-0" />
                  )}
                </button>
              ))}
            </>
          )}

          {/* Loading state */}
          {incidentsLoading && (
            <div className="px-3 py-2 text-sm text-gray-400">
              Loading incidents...
            </div>
          )}

          {/* Empty state */}
          {!incidentsLoading && activeIncidents.length === 0 && plannedIncidents.length === 0 && (
            <div className="px-3 py-2 text-sm text-theme-tertiary">
              No active incidents
            </div>
          )}

          {/* View All Link */}
          <div className="border-t border-gray-700 mt-1 pt-1">
            <Link
              to="/incidents"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary-400 hover:text-primary-300 hover:bg-gray-700"
            >
              View All Incidents
              <FaIcon icon="arrow-right" className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
